import { type LinkProps, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

/** Page size every list endpoint is asked for (backend default and cap: 10 / 100). */
export const PAGE_SIZE = 10

/** 1-based page math for a `{ items, total }` list response. */
export function pageRange(page: number, total: number, perPage = PAGE_SIZE) {
  return {
    lastPage: Math.max(1, Math.ceil(total / perPage)),
    from: total === 0 ? 0 : (page - 1) * perPage + 1,
    to: Math.min(total, page * perPage),
  }
}

/**
 * A page past the end — its last row just deleted, a stale link — is replaced by the last page
 * instead of showing an empty list under a pager that counts rows.
 */
export function useClampedPage(page: number, list: { items: unknown[]; total: number | bigint } | undefined, link: (page: number) => LinkProps) {
  const navigate = useNavigate()
  const lastPage = list ? pageRange(page, Number(list.total)).lastPage : page
  const past = list !== undefined && list.items.length === 0 && page > lastPage
  const latest = useRef(link)
  useEffect(() => {
    latest.current = link
  }, [link])
  useEffect(() => {
    if (past) void navigate({ ...latest.current(lastPage), replace: true })
  }, [past, lastPage, navigate])
}
